import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Trash2, Download, Server } from 'lucide-react';

const PrivacyPage = () => {
  const lastUpdated = 'January 1, 2025';

  return (
    <main className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-green-400 to-emerald-500 
                        flex items-center justify-center mb-6 shadow-lg shadow-green-500/30">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-4">
            Privacy Policy
          </h1>
          <p className="text-midnight-400">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        {/* Privacy Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          {[
            { icon: Server, title: 'No Servers', desc: 'Your data stays on your device only' },
            { icon: Eye, title: 'No Tracking', desc: 'Zero analytics or user tracking' },
            { icon: Lock, title: 'Encrypted', desc: 'Local data is encrypted' },
          ].map((item, i) => (
            <div key={i} className="bg-green-50 rounded-2xl p-6 text-center border border-green-100">
              <item.icon className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-midnight-500 mb-1">{item.title}</h3>
              <p className="text-sm text-midnight-400">{item.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl shadow-midnight-500/5 border border-cyclara-100 p-8 md:p-12 space-y-8"
        >
          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">Our Privacy Commitment</h2>
            <p className="text-midnight-400">
              At Cyclara, we believe your health data is deeply personal and should remain entirely yours. 
              That's why we built Cyclara to be <strong>100% offline</strong> — your data never leaves your device, 
              and we have no way to access it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">Data We Collect</h2>
            <div className="bg-green-50 rounded-xl p-6 border border-green-100">
              <p className="text-green-800 font-semibold mb-2">Short answer: None.</p>
              <p className="text-green-700">
                Cyclara does not collect, transmit, or store any personal data on external servers. 
                All information you enter in the app stays exclusively on your device.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">Data Stored Locally</h2>
            <p className="text-midnight-400 mb-4">
              The following data is stored locally on your device and is never transmitted:
            </p>
            <ul className="space-y-3">
              {[
                'Period tracking data (dates, flow intensity)',
                'Mood and symptom logs',
                'Chat conversations with the AI companion',
                'Personal preferences and settings',
                'Cycle statistics and predictions',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-midnight-400">
                  <Lock className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">AI Processing</h2>
            <p className="text-midnight-400">
              The AI companion in Cyclara runs entirely on your device using a local language model. 
              Your conversations are processed locally and are never sent to any server. 
              The AI has no internet connectivity and cannot transmit information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">Your Rights</h2>
            <p className="text-midnight-400 mb-4">
              Since your data never leaves your device, you have complete control:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-cyclara-50 rounded-xl">
                <Download className="w-5 h-5 text-cyclara-500 mt-0.5" />
                <div>
                  <p className="font-semibold text-midnight-500">Export Data</p>
                  <p className="text-sm text-midnight-400">Download all your data as a JSON file</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-cyclara-50 rounded-xl">
                <Trash2 className="w-5 h-5 text-cyclara-500 mt-0.5" />
                <div>
                  <p className="font-semibold text-midnight-500">Delete Data</p>
                  <p className="text-sm text-midnight-400">Permanently delete all data anytime</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">Website Feedback</h2>
            <p className="text-midnight-400">
              When you submit feedback through our website, we collect your name, email, and message 
              to respond to your inquiry. This information is stored securely and is not shared with third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">Contact Us</h2>
            <p className="text-midnight-400">
              If you have any questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:privacy@cyclara.app" className="text-cyclara-500 hover:underline">
                privacy@cyclara.app
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </main>
  );
};

export default PrivacyPage;
